import PropTypes from '~/lib/propTypes'
import {
  DialogWindow,
  DialogHeader,
  DialogAdornment,
  useDialog,
  ContentTransition,
  DialogBody,
  color,
} from '@ticketswap/solar'
import { ChevronLeftAlt, CloseAlt } from '@ticketswap/solar/icons'
import { useTranslation } from '~/components/TranslationContext'
import {
  Login,
  SignupSocial,
  LoginLinkSent,
  SignupEmail,
  LoginWithCode,
  SignupWithCode,
  AccountVerification,
} from '~/components/Authentication'
import { createMarkup } from '~/utils/createMarkup'
import { useState } from 'react'
import { VerifyEmailConfirmation } from '~/components/SocialAccountsInformationDialog'
import styled from '@emotion/styled'
import { setCookie } from 'cookies-next'
import { YEAR_IN_SECONDS } from '~/lib/timeInSeconds'
import useFeatureFlag from '~/lib/optimizely/useFeatureFlag'
import { useRouter } from 'next/router'

const ActionButton = styled.button`
  color: ${color.foreground};
`

const initialView = 'login'

const AuthDialog = ({
  children,
  onToggle = () => null,
  onComplete = () => null,
  emailLoginRedirectUrl,
}) => {
  const { t } = useTranslation()
  const { locale } = useRouter()
  const [title, setTitle] = useState('')
  const [back, setBack] = useState('')
  const [method, setMethod] = useState('')
  const [activeView, setActiveView] = useState(initialView)
  const { variant } = useFeatureFlag({
    featureKey: 'verification_flow_enabled',
    overrides: {
      overrideAttributes: {
        domainLocale: locale,
      },
    },
  })

  const { show, hide, getToggleProps, getWindowProps } = useDialog({
    onToggle: on => {
      onToggle(on)

      if (on) {
        setActiveView(initialView)
      }
    },
  })

  const onAuthenticationComplete = (
    method,
    withEmailVerify = false,
    withAccountVerification = false
  ) => {
    const verificationFlowEnabled = variant === 'on'

    setCookie('lastLoginMethod', method, {
      maxAge: 2 * YEAR_IN_SECONDS,
      secure: true,
      sameSite: 'lax',
    })

    if (withAccountVerification && verificationFlowEnabled) {
      return setActiveView('accountVerification')
    }

    onComplete()

    if (withEmailVerify) {
      return setActiveView('verifyEmail')
    }

    hide()
  }

  const onSocialSignup = ({ firstName: firstname }, method) => {
    setActiveView('signupSocial')
    setMethod(method)
    setTitle(
      <span
        dangerouslySetInnerHTML={createMarkup(
          t('common:generic.hi', {
            firstname,
          }),
          {
            renderInline: true,
          }
        )}
      />
    )
  }

  const activeViewIsSignupSocial = activeView === 'signupSocial'

  return (
    <>
      {children({ show, getToggleProps })}

      <DialogWindow
        {...getWindowProps()}
        onClick={e => {
          e.preventDefault()
          !activeViewIsSignupSocial && hide()
        }}
      >
        <DialogHeader>
          {back && (
            <DialogAdornment left>
              <ActionButton onClick={() => setActiveView(back)}>
                <ChevronLeftAlt size={24} />
              </ActionButton>
            </DialogAdornment>
          )}
          {title}

          {!activeViewIsSignupSocial && (
            <DialogAdornment right>
              <ActionButton onClick={hide}>
                <CloseAlt size={24} />
              </ActionButton>
            </DialogAdornment>
          )}
        </DialogHeader>
        <ContentTransition
          onChange={({ props: { back, title } }) => {
            title && setTitle(title)

            if (typeof back === 'undefined') {
              return setBack('')
            }

            setBack(back)
          }}
          activeView={activeView}
        >
          <DialogBody key="login" title={t('common:auth.login.title')}>
            <Login
              t={t}
              redirectUrl={emailLoginRedirectUrl}
              onEmailLogin={() => setActiveView('loginLinkSent')}
              onEmailSignup={() => setActiveView('signupEmail')}
              onSocialLogin={onAuthenticationComplete}
              onSocialSignup={onSocialSignup}
              onLoginWithCode={() => setActiveView('loginWithCode')}
              onSignupWithCode={() => setActiveView('signupWithCode')}
            />
          </DialogBody>

          <DialogBody key="loginLinkSent" title={t('common:auth.loginLinkSent.title')} back="login">
            <LoginLinkSent t={t} />
          </DialogBody>

          <DialogBody key="loginWithCode" title={t('common:auth.loginLinkSent.title')} back="login">
            <LoginWithCode t={t} onAuthenticationComplete={onAuthenticationComplete} />
          </DialogBody>

          <DialogBody key="signupEmail" title={t('common:auth.signupEmail.title')} back="login">
            <SignupEmail t={t} onSignupComplete={onAuthenticationComplete} />
          </DialogBody>

          <DialogBody key="signupWithCode" title={t('common:auth.signupCode.title')} back="login">
            <SignupWithCode t={t} onSignupComplete={onAuthenticationComplete} />
          </DialogBody>

          <DialogBody key="signupSocial">
            <SignupSocial
              t={t}
              onSignupComplete={() => onAuthenticationComplete(method)}
              hide={hide}
              method={method}
            />
          </DialogBody>

          <DialogBody key="accountVerification" title={t('common:auth.accountVerification.title')}>
            <AccountVerification t={t} onComplete={onComplete} hide={hide} />
          </DialogBody>

          <VerifyEmailConfirmation
            hide={hide}
            t={t}
            key="verifyEmail"
            title={t('common:socialConnect.dialog.verifyEmailConfirmation.title')}
          />
        </ContentTransition>
      </DialogWindow>
    </>
  )
}

AuthDialog.propTypes = {
  onToggle: PropTypes.func,
  onComplete: PropTypes.func,
  emailLoginRedirectUrl: PropTypes.string,
}

export default AuthDialog
